<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import MoreActions from './MoreActions.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
  isSidebarOpen: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['toggleSidebar']);
const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex items-center w-full min-w-0 h-[48px] px-3 bg-white dark:bg-n-solid-3 flex-shrink-0 gap-1"
  >
    <!-- Left: back + warnings -->
    <div class="flex items-center gap-1 min-w-0 flex-1">
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="flex-shrink-0"
      />
      <fluent-icon
        v-if="!isHMACVerified"
        v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
        size="14"
        class="text-n-amber-10 min-w-[14px] flex-shrink-0"
        icon="warning"
      />
      <span
        v-if="isSnoozed"
        class="text-[11px] font-medium text-n-amber-10 truncate flex-shrink-0"
      >
        {{ snoozedDisplayText }}
      </span>
    </div>

    <!-- Right: SLA + actions + sidebar toggle -->
    <div class="flex items-center gap-0.5 flex-shrink-0">
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <MoreActions :conversation-id="currentChat.id" />
      <button
        class="size-8 rounded-lg hover:bg-n-alpha-2 grid place-content-center text-n-slate-11 cursor-pointer transition-colors"
        :title="isSidebarOpen ? 'Fechar painel' : 'Abrir painel'"
        @click="emit('toggleSidebar')"
      >
        <span
          :class="
            isSidebarOpen
              ? 'i-lucide-panel-right-close'
              : 'i-lucide-panel-right-open'
          "
          class="size-4"
        />
      </button>
    </div>
  </div>
</template>
